// SharePoint file activity for Generate (spec §3.3, D4): which files in the client’s folder changed
// during the update period, how often, and by whom. Never reads file contents. Links stay in the
// browser (returned separately) — they are never sent to the drafting service.
import { addDays, localDate } from "./period.js";

// Guest users sometimes have their email address as display name; never send an address on.
const nameOnly = (n) => (typeof n === "string" && n && !n.includes("@") ? n : null);

const MAX_DEPTH = 6;
const MAX_ITEMS = 500;
const VERSION_LOOKUPS = 25;
const MAX_FILES = 40;
const PARTIAL = "SharePoint: the folder is large — only part of it was scanned.";

export function encodeSharingUrl(url) {
  const b64 = btoa(String.fromCharCode(...new TextEncoder().encode(url)));
  return "u!" + b64.replace(/=+$/, "").replace(/\//g, "_").replace(/\+/g, "-");
}

const NO_RESPONSE = "SharePoint skipped — SharePoint didn’t respond.";

export async function collectFileActivity({ graphGet, sharingUrl, periodStart, periodEnd, deadline = null, concurrency = 5 }) {
  const links = new Map();
  if (!sharingUrl || !String(sharingUrl).trim()) return { files: [], links, notice: "SharePoint skipped — this client has no SharePoint folder link on its Overview tab." };
  let root;
  try {
    root = await graphGet(`/shares/${encodeSharingUrl(String(sharingUrl).trim())}/driveItem?$select=id,name,folder,file,parentReference`);
  } catch (e) {
    const denied = e?.status === 400 || e?.status === 401 || e?.status === 403 || e?.status === 404;
    return { files: [], links, notice: denied ? "SharePoint skipped — couldn’t open the client’s folder link (check the link and your access)." : NO_RESPONSE };
  }
  if (!root?.folder) return { files: [], links, notice: "SharePoint skipped — the client’s SharePoint link isn’t a folder." };

  // A missing driveId means nothing underneath can be fetched at all — report it the same as a
  // dead mailbox/Graph, not as a merely-partial scan.
  const driveId = root.parentReference?.driveId;
  if (!driveId) return { files: [], links, notice: NO_RESPONSE };

  const start = new Date(`${periodStart}T00:00:00`).getTime();
  const endExcl = new Date(`${addDays(periodEnd, 1)}T00:00:00`).getTime();
  const inPeriod = (iso) => { const t = Date.parse(iso); return t >= start && t < endExcl; };
  const childrenUrl = (id) => `/drives/${driveId}/items/${id}/children?$select=id,name,folder,file,lastModifiedDateTime,lastModifiedBy,webUrl&$top=200`;
  const deadlinePassed = () => deadline != null && Date.now() >= deadline;

  const found = [];
  let seen = 0;
  let partial = false;
  const queue = [];

  // Returns true once MAX_ITEMS is hit mid-page, so the caller can stop everything else too.
  const processPage = (dir, page) => {
    if (page.truncated) partial = true;
    for (const it of page.value || []) {
      if (++seen > MAX_ITEMS) { partial = true; return true; }
      const path = dir.path ? `${dir.path}/${it.name}` : it.name;
      if (it.folder) {
        if (dir.depth + 1 <= MAX_DEPTH) queue.push({ id: it.id, depth: dir.depth + 1, path });
        else partial = true;
        continue;
      }
      if (it.file && inPeriod(it.lastModifiedDateTime)) {
        found.push({ itemId: it.id, name: it.name, folder: dir.path, lastModifiedIso: it.lastModifiedDateTime, editedBy: nameOnly(it.lastModifiedBy?.user?.displayName), webUrl: it.webUrl || null });
      }
    }
    return false;
  };

  // The root's own children listing is fetched on its own, outside the concurrency-batched walk
  // below: if it fails, there's nothing to report "partially" — the whole scan never got going.
  let rootPage;
  try {
    rootPage = await graphGet(childrenUrl(root.id), { all: true });
  } catch {
    return { files: [], links, notice: NO_RESPONSE };
  }
  if (processPage({ id: root.id, depth: 0, path: "" }, rootPage)) queue.length = 0;

  // Walk the rest breadth-first, up to `concurrency` child listings in flight at once. If the
  // deadline passes, stop starting new batches and return whatever was found so far.
  while (queue.length) {
    if (deadlinePassed()) { partial = true; break; }
    const batch = queue.splice(0, concurrency);
    const pages = await Promise.all(batch.map((dir) =>
      graphGet(childrenUrl(dir.id), { all: true }).then((page) => ({ dir, page })).catch(() => ({ dir, page: null })),
    ));
    let capHit = false;
    for (const { dir, page } of pages) {
      if (!page) { partial = true; continue; }
      if (processPage(dir, page)) { capHit = true; break; }
    }
    if (capHit) { queue.length = 0; break; }
  }

  found.sort((a, b) => (a.lastModifiedIso < b.lastModifiedIso ? 1 : -1));
  const toLookUp = found.slice(0, VERSION_LOOKUPS);
  for (let i = 0; i < toLookUp.length; i += concurrency) {
    if (deadlinePassed()) { partial = true; break; }
    const batch = toLookUp.slice(i, i + concurrency);
    await Promise.all(batch.map(async (f) => {
      try {
        const v = await graphGet(`/drives/${driveId}/items/${f.itemId}/versions?$select=id,lastModifiedDateTime`, { all: true });
        f.versionsInPeriod = Math.max(1, (v.value || []).filter((x) => inPeriod(x.lastModifiedDateTime)).length);
      } catch { f.versionsInPeriod = null; }
    }));
  }
  const files = found.slice(0, MAX_FILES).map((f, i) => {
    const n = String(i + 1);
    if (f.webUrl) links.set(n, f.webUrl);
    return { id: `file:${n}`, name: f.name, folder: f.folder, lastModified: localDate(new Date(f.lastModifiedIso)), editedBy: f.editedBy, versionsInPeriod: f.versionsInPeriod ?? null };
  });
  if (found.length > MAX_FILES) partial = true;
  return { files, links, notice: partial ? PARTIAL : null };
}
